import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  TableSortLabel,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add,
  Edit,
  Send,
  CheckCircle,
  Cancel,
  Description,
  Delete,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { generateAssemblyCode } from '../components/componentFormUtils';

/**
 * Get "Remaining" display from assembly's timeSinceRecords.
 * - No records or none with remaining → 'NIL'
 * - Each record with remainingTime: "name: value unit" (unit optional)
 * - Multiple entries shown with " · " separator for small-font display.
 * Handles: missing/empty timeSinceRecords, optional remainingTime, missing label/type, empty value.
 */
function getRemainingDisplay(assembly) {
  if (!assembly) return 'NIL';
  const records = assembly.timeSinceRecords;
  if (!records || !Array.isArray(records) || records.length === 0) return 'NIL';

  const parts = records
    .map((r, index) => {
      const remaining = r?.remainingTime;
      if (!remaining || remaining.value == null) return null;
      const valueStr = String(remaining.value).trim();
      if (valueStr === '') return null;
      const name = (r.label && String(r.label).trim()) || r.type || `Record ${index + 1}`;
      const unit = remaining.unit != null ? String(remaining.unit).trim() : '';
      return unit ? `${name}: ${valueStr} ${unit}` : `${name}: ${valueStr}`;
    })
    .filter(Boolean);

  if (parts.length === 0) return 'NIL';
  return parts.join(' · ');
}

const AssemblyTable = ({
  selectedAircraft,
  assemblies,
  loading,
  canEdit,
  canApproveAction,
  getStatusColor,
  onAddAssembly,
  onEditAssembly,
  onSubmitForApproval,
  onApprove,
  onReject,
  onDeleteAssembly,
  onOpenDocumentDialog,
  onRowClick
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('type');
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [activeFilter, setActiveFilter] = useState('ALL');

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const typeOptions = useMemo(() => {
    const set = new Set();
    assemblies.forEach((assembly) => {
      const name = assembly.type?.name || assembly.typeName;
      if (name) set.add(name);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [assemblies]);

  const statusOptions = useMemo(() => {
    const set = new Set();
    assemblies.forEach((assembly) => {
      if (assembly.status) set.add(assembly.status);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [assemblies]);

  const handleTypeFilterChange = (event) => {
    setTypeFilter(event.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleActiveFilterChange = (event) => {
    setActiveFilter(event.target.value);
    setPage(0);
  };

  const filteredAssemblies = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return assemblies.filter((assembly) => {
      if (typeFilter !== 'ALL') {
        const typeName = assembly.type?.name || assembly.typeName || '';
        if (typeName !== typeFilter) return false;
      }
      if (statusFilter !== 'ALL') {
        if ((assembly.status || '') !== statusFilter) return false;
      }
      if (activeFilter !== 'ALL') {
        const isActive = assembly.isActive !== false;
        if (activeFilter === 'ACTIVE' && !isActive) return false;
        if (activeFilter === 'ARCHIVED' && isActive) return false;
      }

      if (!term) return true;

      const code = generateAssemblyCode(assembly);
      const values = [
        code,
        assembly.type?.name || assembly.typeName || '',
        assembly.serialNumber || '',
        assembly.position || '',
        assembly.manufacturerId?.manufacturerName?.name || '',
        assembly.modelId?.name || '',
        assembly.status || '',
        assembly.isActive !== false ? 'active' : 'archived',
        getRemainingDisplay(assembly)
      ];
      return values.some((v) => v.toLowerCase().includes(term));
    });
  }, [assemblies, searchTerm, typeFilter, statusFilter, activeFilter]);

  const handleSortRequest = useCallback((columnId) => {
    setPage(0);
    setSortBy((prev) => {
      if (prev === columnId) {
        setSortOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortOrder('asc');
      return columnId;
    });
  }, []);

  const sortedAssemblies = useMemo(() => {
    const list = [...filteredAssemblies];
    const mult = sortOrder === 'asc' ? 1 : -1;

    const getSortValue = (assembly, key) => {
      switch (key) {
        case 'code':
          return (generateAssemblyCode(assembly) || '').toLowerCase();
        case 'type':
          return (assembly.type?.name || assembly.typeName || '').toLowerCase();
        case 'serialNumber':
          return (assembly.serialNumber || '').toLowerCase();
        case 'position':
          return (assembly.position || '').toLowerCase();
        case 'manufacturer':
          return (assembly.manufacturerId?.manufacturerName?.name || '').toLowerCase();
        case 'model':
          return (assembly.modelId?.name || '').toLowerCase();
        case 'status':
          return (assembly.status || '').toLowerCase();
        case 'active':
          return assembly.isActive !== false ? 'active' : 'archived';
        case 'remaining':
          return getRemainingDisplay(assembly).toLowerCase();
        default:
          return '';
      }
    };

    list.sort((a, b) => {
      const va = getSortValue(a, sortBy);
      const vb = getSortValue(b, sortBy);
      return mult * String(va ?? '').localeCompare(String(vb ?? ''), undefined, { numeric: true, sensitivity: 'base' });
    });

    return list;
  }, [filteredAssemblies, sortBy, sortOrder]);

  const paginatedAssemblies = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedAssemblies.slice(start, start + rowsPerPage);
  }, [sortedAssemblies, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, assemblies.length]);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Assemblies for {selectedAircraft.registration}
        </Typography>
        {canEdit && (
          <Button variant="contained" startIcon={<Add />} onClick={onAddAssembly}>
            Add Assembly
          </Button>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search assemblies..."
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ minWidth: 220, maxWidth: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Assembly Type</InputLabel>
          <Select
            value={typeFilter}
            label="Assembly Type"
            onChange={handleTypeFilterChange}
          >
            <MenuItem value="ALL">All</MenuItem>
            {typeOptions.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={handleStatusFilterChange}
          >
            <MenuItem value="ALL">All</MenuItem>
            {statusOptions.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Active</InputLabel>
          <Select
            value={activeFilter}
            label="Active"
            onChange={handleActiveFilterChange}
          >
            <MenuItem value="ALL">All</MenuItem>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="ARCHIVED">Archived</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={sortBy === 'code' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'code'}
                  direction={sortBy === 'code' ? sortOrder : 'asc'}
                  onClick={() => handleSortRequest('code')}
                >
                  Assembly ID
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'type' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'type'}
                  direction={sortBy === 'type' ? sortOrder : 'asc'}
                  onClick={() => handleSortRequest('type')}
                >
                  Type
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'serialNumber' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'serialNumber'}
                  direction={sortBy === 'serialNumber' ? sortOrder : 'asc'}
                  onClick={() => handleSortRequest('serialNumber')}
                >
                  Serial Number
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'position' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'position'}
                  direction={sortBy === 'position' ? sortOrder : 'asc'}
                  onClick={() => handleSortRequest('position')}
                >
                  Position
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'manufacturer' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'manufacturer'}
                  direction={sortBy === 'manufacturer' ? sortOrder : 'asc'}
                  onClick={() => handleSortRequest('manufacturer')}
                >
                  Manufacturer
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'model' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'model'}
                  direction={sortBy === 'model' ? sortOrder : 'asc'}
                  onClick={() => handleSortRequest('model')}
                >
                  Model
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'status' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'status'}
                  direction={sortBy === 'status' ? sortOrder : 'asc'}
                  onClick={() => handleSortRequest('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'active' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'active'}
                  direction={sortBy === 'active' ? sortOrder : 'asc'}
                  onClick={() => handleSortRequest('active')}
                >
                  Active/Archived
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'remaining' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'remaining'}
                  direction={sortBy === 'remaining' ? sortOrder : 'asc'}
                  onClick={() => handleSortRequest('remaining')}
                >
                  Remaining
                </TableSortLabel>
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center">Loading...</TableCell>
              </TableRow>
            ) : filteredAssemblies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">No assemblies found</TableCell>
              </TableRow>
            ) : (
              paginatedAssemblies.map((assembly) => (
                <TableRow
                  key={assembly._id}
                  hover
                  onClick={onRowClick ? () => onRowClick(assembly) : undefined}
                  sx={{
                    cursor: onRowClick ? 'pointer' : 'default',
                    '&:hover': onRowClick ? { backgroundColor: 'action.hover' } : undefined,
                    opacity: assembly.isActive === false ? 0.7 : 1
                  }}
                >
                  <TableCell sx={{ fontFamily: 'monospace' }}>
                    {generateAssemblyCode(assembly) || '—'}
                  </TableCell>
                  <TableCell>
                    {assembly.type?.name || assembly.typeName || '-'}
                  </TableCell>
                  <TableCell>{assembly.serialNumber}</TableCell>
                  <TableCell>{assembly.position}</TableCell>
                  <TableCell>{assembly.manufacturerId?.manufacturerName?.name || '-'}</TableCell>
                  <TableCell>{assembly.modelId?.name || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={assembly.status}
                      color={getStatusColor(assembly.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={assembly.isActive !== false ? 'Active' : 'Archived'}
                      color={assembly.isActive !== false ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell
                    sx={{ fontSize: '0.75rem', maxWidth: 260 }}
                    title={getRemainingDisplay(assembly)}
                  >
                    {getRemainingDisplay(assembly)}
                  </TableCell>
                  <TableCell>
                    {canEdit && (
                      <>
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); onEditAssembly(assembly); }}
                          title="Edit Assembly"
                        >
                          <Edit />
                        </IconButton>
                        {assembly.status === 'DRAFT' && (
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onSubmitForApproval(assembly._id); }}
                            title="Submit for Approval"
                          >
                            <Send />
                          </IconButton>
                        )}
                        {onDeleteAssembly && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => { e.stopPropagation(); onDeleteAssembly(assembly); }}
                            title={assembly.isActive ? "Archive Assembly" : "Permanently Delete Assembly"}
                          >
                            <Delete />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); onOpenDocumentDialog('ASSEMBLY', assembly); }}
                          title="Manage Documents"
                        >
                          <Description />
                        </IconButton>
                      </>
                    )}
                    {canApproveAction && assembly.status === 'SUBMITTED' && (
                      <>
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => onApprove(assembly._id)}
                          title="Approve Assembly"
                        >
                          <CheckCircle />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => onReject(assembly._id)}
                          title="Reject Assembly"
                        >
                          <Cancel />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {sortedAssemblies.length > 0 && (
          <TablePagination
            component="div"
            count={sortedAssemblies.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`}
          />
        )}
      </TableContainer>
    </>
  );
};

export default AssemblyTable;

